import { birthDateValidation, fromDatePickerFormat, toDatePickerFormat } from '../../../../../../utils/dateHelpers';
import { updatePersonalInformationThunk } from '../../../../../../store/workspace/thunks';
import PhoneNumberInput from '../../../../../../components/PhoneNumberInput';
import { DayValue } from '@amir04lm26/react-modern-calendar-date-picker';
import { errorMessages } from '../../../../../../config/notification';
import { useDispatch, useSelector } from '../../../../../../store/store';
import DatePicker from '../../../../../../components/DatePicker';
import Modal from '../../../../../../components/Modal';
import Input from '../../../../../../components/Input';
import styles from './style.module.scss';
import { useState } from 'react';
import {
    selectUserAttributes,
    selectUpdatingProfileData,
} from '../../../../../../store/workspace/selectors';

interface Props {
    onClose: () => void;
};

/** Edits the applicant's Cognito attributes — the email is fixed and shown read only. */
function PersonalInformationForm({ onClose }: Props) {
    const dispatch = useDispatch();
    const user = useSelector(selectUserAttributes);
    const isSaving = useSelector(selectUpdatingProfileData);

    const email = user?.email || '';
    const [address, setAddress] = useState(user?.address || '');
    const [city, setCity] = useState(user?.['custom:city'] || '');
    const [state, setState] = useState(user?.['custom:state'] || '');
    const [lastName, setLastName] = useState(user?.family_name || '');
    const [firstName, setFirstName] = useState(user?.given_name || '');
    const [country, setCountry] = useState(user?.['custom:country'] || '');
    const [zipCode, setZipCode] = useState(user?.['custom:zipCode'] || '');
    const [phoneNumber, setPhoneNumber] = useState<string | undefined>(user?.phone_number);
    const [linkedinLink, setLinkedinLink] = useState(user?.['custom:linkedinLink'] || '');
    const [preferredName, setPreferredName] = useState(user?.['custom:preferredName'] || '');
    const [DOB, setDOB] = useState<DayValue>(user?.birthdate ? toDatePickerFormat(user.birthdate) : null);

    const submit = async () => {
        try {
            await dispatch(updatePersonalInformationThunk({
                city,
                email,
                state,
                zipCode,
                address,
                country,
                lastName,
                firstName,
                phoneNumber,
                linkedinLink,
                preferredName,
                birthDate: fromDatePickerFormat(DOB),
            })).unwrap();

            onClose();
        } catch {
            // The rejected thunk already reported the failure.
        }
    };

    const isIncomplete =
        !firstName ||
        !lastName ||
        !address ||
        !state ||
        !city ||
        !phoneNumber ||
        !email ||
        !birthDateValidation(DOB);

    return (
        <Modal
            size='md'
            actions='confirm'
            closeFunc={onClose}
            childClassName={styles.wrapper}
            title='Edit personal information'
            confirmButton={{
                text: 'Save',
                type: 'Primary',
                onClick: submit,
                loading: isSaving,
                disabled: isIncomplete,
            }}
        >
            <Input
                type='text'
                placeholder=''
                label='First name'
                value={firstName}
                onChange={setFirstName}
            />
            <Input
                type='text'
                placeholder=''
                label='Last name'
                value={lastName}
                onChange={setLastName}
            />
            <Input
                type='text'
                placeholder=''
                value={preferredName}
                onChange={setPreferredName}
                label='Preferred name (Optional)'
            />
            <PhoneNumberInput
                disabled
                showErrorMessage
                label='Phone number'
                phoneNumber={phoneNumber}
                setPhoneNumber={setPhoneNumber}
            />
            <Input
                disabled
                label='Email'
                type='email'
                value={email}
                placeholder=''
                validation='Email'
            />
            <DatePicker
                showErrorMessage
                selectedDate={DOB}
                label='Date of birth'
                setSelectedDate={setDOB}
                placeholder='MM-DD-YYYY'
                errorMessage={(DOB && !birthDateValidation(DOB)) ? errorMessages.birthDate : ''}
            />
            <Input
                type='text'
                label='Address'
                placeholder=''
                value={address}
                onChange={setAddress}
            />
            <Input
                type='text'
                label='City'
                value={city}
                placeholder=''
                onChange={setCity}
            />
            <Input
                type='text'
                placeholder=''
                label='Country'
                value={country}
                onChange={setCountry}
            />
            <div className={styles.state_zip}>
                <Input
                    type='text'
                    label='State'
                    value={state}
                    placeholder=''
                    onChange={setState}
                    wrapperClassName={styles.state_zip_state}
                />
                <Input
                    type='text'
                    maxLength={7}
                    placeholder=''
                    label='Zip code'
                    value={zipCode}
                    onChange={setZipCode}
                    wrapperClassName={styles.state_zip_zipCode}
                />
            </div>
            <Input
                type='text'
                placeholder=''
                value={linkedinLink}
                onChange={setLinkedinLink}
                label='Linkedin link (Optional)'
            />
        </Modal>
    );
};

export default PersonalInformationForm;
